<template>
    <table-container>
        <data-iterator
            ref="iterator"
            :api="api"
            :filters.sync="filters"
            @update:filters="updateFilters"
        >
            <template v-slot:filters="{ busy }">
                <table-filters
                    v-model="filters"
                    :fields="isCompanyAdmin ? companyAdminHeaders : headers"
                    :busy="busy"
                    :list-filters="
                        isCompanyAdmin ? companyAdminFilters : listFilters
                    "
                    @input="updateFilters"
                ></table-filters>
            </template>

            <template slot-scope="{ items }">
                <d-table
                    :items="items"
                    :headers="isCompanyAdmin ? companyAdminHeaders : headers"
                    :filters.sync="filters"
                    :row-action="isSuperAdmin || isCompanyAdmin"
                    class="orders-table"
                    @onRowSelected="onRowSelected"
                >
                    <!-- createdAt -->
                    <template v-slot:[`item.createdAt`]="{ item }">
                        <identifier
                            :item="item"
                            :get-id="getIdFromIri"
                            :show-edit-link="isSuperAdmin || isCompanyAdmin"
                            :route-type="routeType"
                        ></identifier>
                        <warning
                            v-if="
                                isSuperAdmin &&
                                ((item.deliveryJob &&
                                    item.deliveryJob.warning) ||
                                    item.warning)
                            "
                            :item="item"
                            @execute="removeWarning(item.id)"
                        ></warning>
                        <deliverect-chips :item="item"></deliverect-chips>
                    </template>

                    <!-- status -->
                    <template v-slot:[`item.status`]="{ item }">
                        <status :item="item"></status>
                    </template>

                    <!-- delivery -->
                    <template v-slot:[`item.deliveryJob`]="{ item }">
                        <delivery-status :item="item"></delivery-status>
                    </template>

                    <!-- deliveryJob.pickupTime -->
                    <template v-slot:[`item.deliveryJob.pickupTime`]="{ item }">
                        <router-link
                            v-if="isSuperAdmin"
                            :to="
                                getIdFromIri(
                                    item,
                                    'restaurant',
                                    routeType.RESTAURANT_EDIT,
                                    'id'
                                )
                            "
                        >
                            {{ item.restaurantName }}
                        </router-link>
                        <template v-else>
                            {{ item.restaurantName }}
                        </template>
                        <small
                            v-if="
                                item.deliveryJob &&
                                item.deliveryJob.primaryMethod !==
                                    'restaurant_managed'
                            "
                            class="d-flex flex-nowrap g-73--text"
                        >
                            {{ item.deliveryJob.pickupTime | transformDate }}
                        </small>
                        <non-partner-badge
                            v-if="isSuperAdmin"
                            :item="item"
                        ></non-partner-badge>
                        <third-party-warning :item="item"></third-party-warning>
                    </template>

                    <!-- dropOff -->
                    <template v-slot:[`item.requiredDeliveryTime`]="{ item }">
                        <drop-off :item="item"></drop-off>
                    </template>

                    <!-- deliveryName -->
                    <template v-slot:[`item.deliveryName`]="{ item }">
                        <customer :item="item"></customer>
                    </template>

                    <!-- priceTotal -->
                    <template v-slot:[`item.priceTotal`]="{ item }">
                        <payment :item="item"></payment>
                    </template>

                    <!-- sent -->
                    <template v-slot:[`item.sent`]="{ item }">
                        <div class="d-flex">
                            <flag
                                :active="parseFloat(item.sentRatio || 0) >= 1"
                            ></flag>
                            <v-tooltip
                                v-if="
                                    item.restaurantEmailStatus === 'error' &&
                                    item.restaurantEmailErrorCount >= 3
                                "
                                bottom
                            >
                                <template v-slot:activator="{ on }">
                                    <v-icon class="ml-2" color="red" v-on="on"
                                        >priority_high</v-icon
                                    >
                                </template>
                                {{ $t('orders.restaurant_email_failed') }}
                            </v-tooltip>
                        </div>
                    </template>

                    <!-- cost code -->
                    <template v-slot:[`item.costCode`]="{ item }">
                        <multi-cost-code-col
                            :item="item"
                            @click="editCostCodes(item)"
                        ></multi-cost-code-col>
                    </template>

                    <template v-slot:rowActions="{ item }">
                        <table-actions
                            v-if="isSuperAdmin"
                            :item="item"
                            :update-table-items="updateTableItems"
                        ></table-actions>
                        <company-admin-order-cancel-button
                            v-else-if="
                                isCompanyAdmin &&
                                userHasRoles(ROLE_COMPANY_ORDER_CANCEL)
                            "
                            :item="item"
                        ></company-admin-order-cancel-button>
                    </template>
                </d-table>
            </template>
        </data-iterator>
        <multi-cost-code-edit-modal
            :item="editItem"
            @close="editItem = null"
            @saved="updateTableItems"
        ></multi-cost-code-edit-modal>
    </table-container>
</template>

<script>
import RestApiType from '@/api/RestApiType'
import routeType from '@/router/routeType'
import OrdersApiClient from '@/api/RestApi/OrdersApiClient'
import DataIteratorMixin from '@/components/mixins/DataIteratorMixin'
import DataTableMixin from '@/components/mixins/DataTableMixin'
import { getIdFromIri } from '@/services/IdFromIri'
import EditInList from '@/components/mixins/EditInList'
import Headers from '@/pages/orders/mixins/Headers'
import ListFilters from '@/pages/orders/mixins/ListFilters'
import AuthMixin from '@/components/mixins/AuthMixin'
import Status from '@/components/elements/Status.vue'
import Customer from '@/components/elements/Customer.vue'
import Payment from '@/components/elements/Payment.vue'
import Warning from '@/components/elements/Warning.vue'
import Flag from '@/components/elements/Flag.vue'
import DeliveryStatus from '@/components/elements/DeliveryStatus.vue'
import DropOff from '@/components/elements/DropOff.vue'
import TableActions from './TableActions.vue'
import Identifier from './Identifier.vue'
import MultiCostCodeCol from './parts/MultiCostCodeDisplay.vue'
import MultiCostCodeEditModal from './tableParts/MultiCostCodeEditModal.vue'
import ThirdPartyWarning from './ThirdPartyWarning.vue'
import NonPartnerBadge from './NonPartnerBadge.vue'
import DeliverectChips from './tableParts/DeliverectChips.vue'
import CompanyAdminOrderCancelButton from '@/components/pages/orders/parts/CompanyAdminOrderCancelButton.vue'
import { ROLE_COMPANY_ORDER_CANCEL } from '@/enum/roles'
import Config from '@/config.loader'
import { mapGetters } from 'vuex'
import authType from '@/store/type/authType'
import RefreshEventTimer from '@/services/RefreshEventTimer'

export default {
    components: {
        CompanyAdminOrderCancelButton,
        MultiCostCodeCol,
        MultiCostCodeEditModal,
        Identifier,
        TableActions,
        DropOff,
        Flag,
        DeliveryStatus,
        Status,
        Customer,
        Payment,
        Warning,
        ThirdPartyWarning,
        NonPartnerBadge,
        DeliverectChips,
    },
    mixins: [
        DataIteratorMixin,
        DataTableMixin,
        EditInList,
        Headers,
        ListFilters,
        AuthMixin,
    ],
    data() {
        return {
            api: RestApiType.ORDERS,
            routeType: routeType,
            ROLE_COMPANY_ORDER_CANCEL,
            filters: {
                requiredDeliveryTime: {
                    after: this.$date()
                        .startOf('day')
                        .subtract(90, 'day')
                        .format(),
                },
                order: {
                    createdAt: 'desc',
                },
            },
            editItem: null,
            updateEvents: null,
            refreshEvents: null,
        }
    },
    computed: {
        tableName() {
            return this.$t('labels.orders')
        },
        ...mapGetters({
            jwt: authType.getters.GET_JWT,
            companyId: authType.getters.ACTIVE_COMPANY_ID,
        }),
    },

    beforeDestroy() {
        if (this.updateEvents) {
            this.updateEvents.close()
            this.updateEvents = null
        }
        if (this.refreshEvents) {
            this.refreshEvents.close()
            this.refreshEvents = null
        }
    },

    mounted() {
        const topicPrefix = this.isCompanyAdmin
            ? `/company-admin/${this.companyId}`
            : '/admin'

        // = = = update order = = =
        this.updateEvents = new EventSource(
            `${Config.getConfigValue(
                'VUE_APP_MERCURE_HOST'
            )}/.well-known/mercure?topic=${topicPrefix}/order/update&authorization=${
                this.jwt
            }`
        )
        this.updateEvents.onmessage = this.mercureUpdate

        // = = = update list = = =
        this.refreshEvents = new EventSource(
            `${Config.getConfigValue(
                'VUE_APP_MERCURE_HOST'
            )}/.well-known/mercure?topic=${topicPrefix}/order/refresh-list&authorization=${
                this.jwt
            }`
        )

        const refreshEventTimer = new RefreshEventTimer(
            this.mercureRefresh,
            parseInt(Config.getConfigValue('VUE_APP_ORDER_REFRESH_INTVAL_SEC'))
        )
        this.refreshEvents.onmessage = refreshEventTimer.fire

        setInterval(this.interval, 1000 * 120)
    },
    methods: {
        mercureRefresh() {
            if (this.$refs.iterator) this.$refs.iterator.updateItems(true, true)
        },
        mercureUpdate(message) {
            if (this.$refs.iterator)
                this.$refs.iterator.updateItem(JSON.parse(message.data))
        },
        getIdFromIri,
        removeWarning(itemId) {
            OrdersApiClient.removeDeliveryWarning(itemId)
        },
        editCostCodes(item) {
            this.editItem = item
        },
    },
}
</script>
